import React from 'react';
import { motion } from 'framer-motion';

const secrets = [
  { no: "01", title: "การมูขอพรที่ถูกจุด", desc: "วิธีขอพรและแหล่งไหว้ที่เกี่ยวข้องกับการเงินโดยเฉพาะ ไม่ใช่การไหว้แบบทั่วไป" },
  { no: "02", title: "ฮวงจุ้ยส่วนบุคคล", desc: "ปรับใช้ร่วมกับทิศมงคลเสริมทรัพย์ที่คำนวณจากดวงของคุณเองในวันที่ 2" },
  { no: "03", title: "พิธีกรรมเต๋าดึงดูดทรัพย์", desc: "เทคนิคโบราณจากตำราโหราศาสตร์จีนและพิธีกรรมสายเต๋าที่สืบทอดกันมา" },
  { no: "04", title: "พิธีกรรมยุคบาบิโลน", desc: "แนวคิดการดึงดูดความมั่งคั่งจากอารยธรรมโบราณที่เป็นต้นทางของโหราศาสตร์สายตะวันตก" },
  { no: "05", title: "คลื่นความถี่ปรับจูนสมอง", desc: "หลักการทางวิทยาศาสตร์ในการปรับคลื่นสมองให้เข้าสู่ 'คลื่นความมั่งคั่ง' ก่อนตัดสินใจลงทุน" },
  { no: "06", title: "เทคนิคเสริมอื่นๆ", desc: "ที่จะถูกเปิดเผยเฉพาะในห้องเรียนวันจริงเท่านั้น" }
];

export default function SecretKnowledge() {
  return (
    <section className="relative py-24 md:py-32 overflow-hidden bg-black">
      {/* Background Image with Dark Overlay to create a mysterious vibe */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://media-manager-c.questera.ai/greta-media/471e332637c745cac6e5f952a1669a461aea7ff8853c3b4ee7dd2b5f799ce1f00d91c0ccf6cd346bf4393cbd10a6b0de/images/aW1hZ2UvcG5n/c3a030af99e58faabc72d464c8bc0c08.png" 
          alt="Mysterious Astrological Background" 
          className="w-full h-full object-cover object-center opacity-40"
        />
        {/* Gradient overlay to blend image into the dark background smoothly */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/90 backdrop-blur-[2px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header - Centered for Symmetry */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-brand-red font-bold uppercase tracking-[0.3em] text-sm md:text-base mb-6 block drop-shadow-md"
          >
            พิเศษเฉพาะรุ่นนี้เท่านั้น
          </motion.span>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 leading-tight drop-shadow-lg"
          >
            ความรู้ลับเสริมมั่งคั่ง <br className="hidden md:block" />
            <span className="text-zinc-300 font-medium text-2xl md:text-4xl mt-4 block">ที่นักลงทุนระดับบนไม่เคยเปิดเผย</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-zinc-400 font-light leading-relaxed max-w-3xl mx-auto"
          >
            อ.รวิ ได้รวบรวมเทคนิคเพิ่มพลังความมั่งคั่งจากหลายสาย ที่นักลงทุนและนักธุรกิจที่ประสบความสำเร็จมักไม่กล้าเปิดเผยต่อสาธารณะ
          </motion.p>
        </div>

        {/* Grid - Centered items with Glassmorphism effect */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {secrets.map((secret, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-black/40 backdrop-blur-md border border-white/10 p-10 rounded-2xl hover:bg-white/5 hover:border-brand-red/50 transition-all duration-500 group flex flex-col items-center text-center shadow-2xl"
            >
              <div className="text-brand-red/70 font-black text-5xl mb-6 group-hover:text-brand-red group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500 drop-shadow-md">
                {secret.no}
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4 tracking-wide group-hover:text-brand-red transition-colors">
                {secret.title}
              </h3>
              <p className="text-zinc-400 leading-relaxed font-light text-sm md:text-base">
                {secret.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Disclaimer */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-20 pt-8 border-t border-white/10 text-center"
        >
          <p className="text-zinc-500 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]">
            Disclaimer: เนื้อหาส่วนนี้เป็นความรู้เสริมเชิงวัฒนธรรมและความเชื่อ ใช้ประกอบการตัดสินใจเท่านั้น ไม่ใช่การรับประกันผลลัพธ์ทางการเงิน
          </p>
        </motion.div>

      </div>
    </section>
  );
}